import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetInboxesWithCustom } from '@/api/chat'
import { UserRoleEnum } from '@/models'

import API from '../../api/api'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'

import { CLIENT_CREATION_CANCELLED, TwilioService } from './services/twilio-service'
import { convertConversation } from './helpers'

let hasShownConnectionError = false

const useChatGeneral = ({
  userData,
  selectedBuilding,
  conversations,
  chatContacts,
  dispatchSetConversations,
  dispatchSetLoadingConversations,
  dispatchGetChatContacts,
  dispatchGetChatInboxes,
  getInvoicesWithCount,
  getAllConversationsPerBuildingId,
  dispatchGetCustomChatInboxes,
}) => {
  const { t } = useTranslation(['chat'])
  const { displaySnackbar } = useSnackbar()
  const [rawConversations, setRawConversations] = useState([])
  const selectedBuildingId = selectedBuilding ? selectedBuilding.id : null
  const currentUserId = userData.user ? userData.user.id : null

  // NOTE: prefetch inboxes for widget
  useGetInboxesWithCustom({
    buildingId: selectedBuildingId,
    chatCustom: selectedBuilding?.chatCustom,
  })

  const getChatContactsFromConversationIds = async (convs) => {
    const data = convs.map((conversation) => {
      return Number(
        conversation.channelName
          ? conversation.channelName.slice(0, conversation.channelName.indexOf('_'))
          : conversation.id.slice(0, conversation.id.indexOf('_')),
      )
    })
    const response = await dispatchGetChatContacts(data)
    return response
  }

  // at the start
  useEffect(() => {
    if (rawConversations.length && chatContacts.length) {
      const convertedConversations = convertConversation(rawConversations, { chatContacts })
      dispatchSetConversations(convertedConversations)
      dispatchSetLoadingConversations(false)
    }
  }, [chatContacts.length])

  const doWhenNewConvAppear = async () => {
    try {
      const newConversations = conversations.filter((conv) => !conv.email)
      const personsData = await getChatContactsFromConversationIds(conversations)
      if (personsData.length) {
        const formattedNewConversations = convertConversation(newConversations, {
          chatContacts,
          personsData,
        })
        dispatchSetConversations([...formattedNewConversations, ...conversations])
      }
    } catch (error) {
      console.error('Error in doSmthWhenNewConvAppear:', error)
    }
  }

  // when totally new conv appears
  useEffect(() => {
    ;(async () => {
      if (conversations.length > rawConversations.length) {
        doWhenNewConvAppear()
      }
    })()
  }, [conversations.length])

  useEffect(() => {
    if (rawConversations.length) {
      getChatContactsFromConversationIds(rawConversations)
    }
  }, [rawConversations.length])

  useEffect(() => {
    let isCancelled = false
    ;(async () => {
      if (currentUserId && selectedBuildingId) {
        try {
          await API.getTwilioToken().then((token) =>
            TwilioService.getInstance().getConversationsClient(token, API.getTwilioToken),
          )
          const buildingConversations = await getAllConversationsPerBuildingId(selectedBuildingId)
          setRawConversations(buildingConversations.filter((conv) => conv.lastMessage))
        } catch (error) {
          if (isCancelled || error?.message === CLIENT_CREATION_CANCELLED) return
          console.error('Error initializing chat connection:', error)
          if (!hasShownConnectionError) {
            hasShownConnectionError = true
            displaySnackbar(t('chat:genericErrors.connectionFailed'), true)
          }
        } finally {
          dispatchSetLoadingConversations(false)
        }
      }
    })()
    return () => {
      isCancelled = true
      TwilioService.getInstance().clientShutdown()
    }
  }, [currentUserId, selectedBuildingId])

  useEffect(() => {
    if (selectedBuildingId) {
      dispatchSetLoadingConversations(true)
      dispatchGetChatInboxes(selectedBuildingId)
      dispatchGetCustomChatInboxes(selectedBuildingId)
      if (
        [UserRoleEnum.enum.Administrator, UserRoleEnum.enum.PropertyManager].includes(
          userData.user?.role,
        )
      ) {
        getInvoicesWithCount(1, selectedBuildingId)
      }
    }
  }, [selectedBuildingId])
}

export default useChatGeneral
